import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, PlayCircle, Users, ShieldCheck, Car, Megaphone } from "lucide-react";
import { Logo } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";

const GREEN = "#35A787";

export default function LandingPage() {
  const { config } = useAuth();
  const brand = config?.brand?.name || "Anand";

  return (
    <div className="min-h-screen bg-[#FBFAF7] text-[#1F2937]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-[#EAE9E4] bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1100px] items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <Logo size={26} className="max-w-[130px] object-contain" />
            <span className="font-display text-base font-bold tracking-tight text-[#14130E]">{brand}</span>
          </div>
          <nav className="hidden items-center gap-6 text-sm font-medium text-[#5B5A56] md:flex">
            <a href="#for-advertisers" className="transition-colors hover:text-[#14130E]">Advertise</a>
            <a href="#for-drivers" className="transition-colors hover:text-[#14130E]">Drive with us</a>
            <a href="#how" className="transition-colors hover:text-[#14130E]">How it works</a>
            <a href="#pricing" className="transition-colors hover:text-[#14130E]">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/business" className="hidden rounded-full px-4 py-2 text-sm font-medium text-[#5B5A56] transition-colors hover:text-[#14130E] sm:block">Sign in</Link>
            <Link to="/signup/advertiser" className="tap rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:opacity-90" style={{ background: GREEN }}>Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(800px 400px at 20% -10%, rgba(53,167,135,.12), transparent 55%), radial-gradient(600px 380px at 90% 10%, rgba(255,196,0,.10), transparent 55%)" }} />
        <div className="relative mx-auto max-w-[1100px] px-4 pb-20 pt-16 text-center sm:px-6 sm:pt-24">
          <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ borderColor: "#35A78755", color: "#2D8F74" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-[#35A787]" /> In-cab digital advertising
          </div>
          <h1 className="mx-auto max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-[#14130E] sm:text-6xl">
            Your ad, on the road.<br /><span style={{ color: GREEN }}>Seen thousands of times daily.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#6B6659]">
            {brand} mounts digital screens inside city cabs and plays your ad on loop for riders across Pune. Start in minutes — you pick the screen, we handle the hardware.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup/advertiser" className="tap group inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_24px_-8px_rgba(53,167,135,0.5)] transition-all hover:opacity-90" style={{ background: GREEN }}>
              Advertise with us <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link to="/signup/driver" className="tap inline-flex items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-6 py-3.5 text-sm font-semibold text-[#14130E] shadow-sm transition-all hover:border-[#D1D5DB]">
              Become a driver <ArrowRight size={16} />
            </Link>
          </div>

          {/* Stats */}
          <div className="mx-auto mt-14 flex max-w-2xl justify-center border-t border-[#EAE9E4] pt-8">
            {[["720", "plays / cab / day"]].map(([v, l]) => (
              <div key={l} className="text-center">
                <div className="font-mono text-2xl font-semibold text-[#14130E]">{v}</div>
                <div className="mt-1 text-[11px] uppercase tracking-wider text-[#8A887F]">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For advertisers */}
      <section id="for-advertisers" className="mx-auto max-w-[1100px] px-4 py-16 sm:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2D8F74]"><Megaphone size={14} /> For advertisers</div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-[#14130E]">Put your brand in front of riders</h2>
            <p className="mt-3 text-base leading-relaxed text-[#6B6659]">Each cab screen rotates up to 4 ads a minute. Your creative plays in a loop all day — every ride is an impression, every day is a campaign.</p>
            <ul className="mt-6 space-y-3 text-sm text-[#4B5563]">
              <li className="flex items-start gap-2.5"><TrendingUp size={17} className="mt-0.5 flex-none text-[#35A787]" /> Live, per-cab play counts — see exactly what your budget bought</li>
              <li className="flex items-start gap-2.5"><ShieldCheck size={17} className="mt-0.5 flex-none text-[#35A787]" /> Upload a video, get it reviewed, go on air — all self-serve</li>
            </ul>
            <Link to="/signup/advertiser" className="tap mt-7 inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white transition-all hover:opacity-90" style={{ background: GREEN }}>Create your account <ArrowRight size={16} /></Link>
          </div>
          <div className="rounded-2xl border border-[#EAE9E4] bg-white p-6 shadow-[0_1px_3px_rgba(16,24,40,0.06),0_24px_60px_-28px_rgba(16,24,40,0.3)]">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display text-sm font-semibold text-[#1F2937]">Live dashboard</div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-[#0B6E4F]"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[["₹1.13M", "Monthly revenue"], ["8.2k", "Plays today"], ["17", "Cabs booked"]].map(([v, l]) => (
                <div key={l} className="rounded-xl border border-[#F1F3F5] bg-[#FBFAF7] p-3">
                  <div className="font-mono text-lg font-semibold text-[#14130E]">{v}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-[#8A887F]">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {[["Slot 1", "Pizza Palace"], ["Slot 2", "Kaveri Retail"], ["Slot 3", "Your brand here"]].map(([s, a]) => (
                <div key={s} className="flex items-center justify-between rounded-lg border border-[#F1F3F5] px-3 py-2.5">
                  <span className="text-xs font-medium text-[#8A887F]">{s}</span>
                  <span className="text-xs font-semibold text-[#1F2937]">{a}</span>
                  <span className="flex h-5 min-w-[18px] items-center justify-center rounded-full bg-[#E8F6F1] px-1.5 text-[10px] font-bold text-[#2D8F74]">on air</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <figure className="mt-10 overflow-hidden rounded-2xl border border-[#EAE9E4] bg-white shadow-[0_24px_60px_-28px_rgba(16,24,40,0.3)]">
          <img src="/anand-cab-screen.png" alt="Anand headrest screen live in a Pune cab" className="w-full" />
          <figcaption className="flex items-center gap-2 border-t border-[#EAE9E4] bg-[#FBFAF7] px-5 py-3 text-xs text-[#8A887F]">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> A screen on the road right now — your ad, in front of a rider.
          </figcaption>
        </figure>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-[#EAE9E4] bg-white py-16">
        <div className="mx-auto max-w-[1100px] px-4 sm:px-6">
          <h2 className="text-center font-display text-3xl font-bold tracking-tight text-[#14130E]">How it works</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              ["1", "Create your account", "Sign up as an advertiser with just a name and email."],
              ["2", "Upload your ad", "Send us a 15s video. Ops reviews and approves it — usually in minutes."],
              ["3", "Pick cabs & go live", "Choose zones, request cabs, and your ad starts playing on the road."],
            ].map(([n, t, d]) => (
              <div key={n} className="rounded-2xl border border-[#EAE9E4] bg-[#FBFAF7] p-6">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full font-display text-base font-bold text-white" style={{ background: GREEN }}>{n}</div>
                <div className="font-display text-base font-semibold text-[#14130E]">{t}</div>
                <p className="mt-1.5 text-sm leading-relaxed text-[#6B6659]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For drivers */}
      <section id="for-drivers" className="mx-auto max-w-[1100px] px-4 py-16 sm:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <div className="rounded-2xl border border-[#E5E7EB] bg-slate-950 p-6 shadow-[0_24px_60px_-28px_rgba(16,24,40,0.5)]">
              <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400"><Users size={14} /> Driver app</div>
              <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
                <div className="text-xs text-slate-500">Namaste</div>
                <div className="font-display text-lg font-semibold text-white">Your cab status</div>
                <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
                  You're on the waitlist — we'll assign you a cab when a slot opens.
                </div>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2D8F74]"><PlayCircle size={14} /> For drivers</div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-[#14130E]">Drive, and your cab earns</h2>
            <p className="mt-3 text-base leading-relaxed text-[#6B6659]">We install a screen in your cab, and the ads that play on it earn on your behalf. You keep driving as usual — we handle the rest.</p>
            <ul className="mt-6 space-y-3 text-sm text-[#4B5563]">
              <li className="flex items-start gap-2.5"><Car size={17} className="mt-0.5 flex-none text-[#35A787]" /> Join the waitlist — get assigned when a cab slot opens in your area</li>
              <li className="flex items-start gap-2.5"><ShieldCheck size={17} className="mt-0.5 flex-none text-[#35A787]" /> A small screen in the cab, no change to your driving routine</li>
              <li className="flex items-start gap-2.5"><TrendingUp size={17} className="mt-0.5 flex-none text-[#35A787]" /> Track your status right from the driver app</li>
            </ul>
            <Link to="/signup/driver" className="tap mt-7 inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white transition-all hover:opacity-90" style={{ background: GREEN }}>Join the driver waitlist <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-[#EAE9E4] bg-white py-16">
        <div className="mx-auto max-w-[1100px] px-4 text-center sm:px-6">
          <h2 className="font-display text-3xl font-bold tracking-tight text-[#14130E]">Simple pricing</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-[#6B6659]">One flat rate. Multiple brands share each cab screen, so you only pay for your slot.</p>
          <div className="mx-auto mt-8 max-w-sm rounded-2xl border border-[#EAE9E4] bg-[#FBFAF7] p-8 text-left">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#8A887F]">Per cab / month</div>
            <div className="mt-2 font-display text-5xl font-bold tracking-tight text-[#14130E]">₹4,300</div>
            <p className="mt-1 text-sm text-[#8A887F]">that's per slot — a few brands share each cab screen</p>
            <ul className="mt-5 space-y-2 text-sm text-[#4B5563]">
              <li>• 720 plays per cab per day</li>
              <li>• Includes tablet + installation</li>
              <li>• Live play reports in your dashboard</li>
            </ul>
            <Link to="/signup/advertiser" className="tap mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-semibold text-white transition-all hover:opacity-90" style={{ background: GREEN }}>Start advertising <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#EAE9E4] bg-[#FBFAF7] py-10">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center justify-between gap-4 px-4 text-sm text-[#8A887F] sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <Logo size={20} className="max-w-[100px] object-contain" />
            <span className="font-display font-semibold text-[#14130E]">{brand}</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/business" className="transition-colors hover:text-[#14130E]">Sign in</Link>
            <Link to="/signup/advertiser" className="transition-colors hover:text-[#14130E]">Advertise</Link>
            <Link to="/signup/driver" className="transition-colors hover:text-[#14130E]">Drive</Link>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} {brand} · {config?.brand?.city || "Pune"}</div>
        </div>
      </footer>
    </div>
  );
}